import Controller, { inject as controller } from '@ember/controller';
import { task, restartableTask } from 'ember-concurrency';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { APP_DETAILS__SUB_NAV_TITLES } from 'sh-www/constants/routes/project-details';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { POSTING__APP_SELLER_INCLUDES } from 'sh-www/constants/routes/posting';
import {
  PROJECT_DETAILS__PROGRESSIVE_INCLUDES,
  getProjectDetailsPaymentIncludes,
} from "sh-www/constants/routes/project-details";

/** @module */
class PostingAppController extends Controller {
  @controller('application') applicationController;

  @service('commands/application-commands') applicationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('error-tracker') errorTracker;
  @service session;
  @service store;
  @service media;
  @service router;

  application = null;
  currentSubNavState = APP_DETAILS__SUB_NAV_TITLES.ACTIVITY;
  hasInitializedThreadMessages = false;
  hasUploadsInRoute = false;
  incomingErrors = {};
  newActivityMessage = '';
  profileOverlay = null;
  profileOverlayType = null;
  profileOverlayModel = null;

  @computed('application.price')
  get hasOfferBeenMade() {
    return this.application.price > 0;
  }

  @computed('loadPaymentData.last.isError', 'loadPaymentData.isRunning')
  get disableActionButtons() {
    return this.loadPaymentData.isRunning || this.loadPaymentData.last?.isError;
  }

  @computed('application.legalAgreements.@each.accepted')
  get isLegalSigningNeeded() {
    const legalAgreement = this.application.legalAgreements.objectAt(0);
    return !legalAgreement?.accepted;
  }

  @computed('application.legalAgreements.@each.legal')
  get legalToShow() {
    const legalAgreement = this.application.legalAgreements.objectAt(0);
    return legalAgreement?.legal ?? null;
  }

  @computed('application.pitch', 'currentSubNavState')
  get projectNavItems() {
    const isNavActive = (navTitleToCheck) => navTitleToCheck === this.currentSubNavState;
    const navItems = [
      {
        title: APP_DETAILS__SUB_NAV_TITLES.ACTIVITY,
        isActive: () => isNavActive(APP_DETAILS__SUB_NAV_TITLES.ACTIVITY)
      },
      {
        title: APP_DETAILS__SUB_NAV_TITLES.DETAILS,
        isActive: () => isNavActive(APP_DETAILS__SUB_NAV_TITLES.DETAILS),
      },
    ];

    if (this.application.pitch) {
      navItems.push({
        title: APP_DETAILS__SUB_NAV_TITLES.PITCH,
        isActive: () => isNavActive(APP_DETAILS__SUB_NAV_TITLES.PITCH)
      });
    }

    return navItems;
  }

  @computed('currentSubNavState')
  get showActivity() {
    return this.currentSubNavState === APP_DETAILS__SUB_NAV_TITLES.ACTIVITY;
  }

  @computed('currentSubNavState')
  get showDetails() {
    return this.currentSubNavState === APP_DETAILS__SUB_NAV_TITLES.DETAILS;
  }

  @computed('currentSubNavState')
  get showPitch() {
    return this.currentSubNavState === APP_DETAILS__SUB_NAV_TITLES.PITCH;
  }

  @action
  updateSubNavState(subNavState) {
    this.set('currentSubNavState', subNavState);
  }

  @action
  onProjectNavItemClicked(subNavTitle) {
    switch (subNavTitle) {
      case APP_DETAILS__SUB_NAV_TITLES.ACTIVITY:
        this.updateSubNavState(APP_DETAILS__SUB_NAV_TITLES.ACTIVITY);
        break;
      case APP_DETAILS__SUB_NAV_TITLES.DETAILS:
        this.updateSubNavState(APP_DETAILS__SUB_NAV_TITLES.DETAILS);
        break;
      case APP_DETAILS__SUB_NAV_TITLES.PITCH:
        this.updateSubNavState(APP_DETAILS__SUB_NAV_TITLES.PITCH);
        break;
    }
  }

  @action
  handleProfileLinkClick(profileOverlay, type, model) {
    this.setProperties({
      profileOverlayType: type,
      profileOverlayModel: model,
      'applicationController.profileOverlay': profileOverlay,
    });
  }

  @action
  setHasUploadsInRoute(toggleBool) {
    this.set('hasUploadsInRoute', toggleBool);
  }

  @action
  handleProfileOverlayClose() {
    this.applicationController.set('profileOverlay', null);
  }

  @restartableTask
  *loadThreadData({ id }) {
    const application = yield this.silentlyLoadApplication(id, PROJECT_DETAILS__PROGRESSIVE_INCLUDES.THREAD);
    this.setProperties({
      application,
      hasInitializedThreadMessages: true
    });
  }

  @restartableTask
  *loadPaymentData({ id }) {
    const application = yield this.silentlyLoadApplication(id, getProjectDetailsPaymentIncludes(this.session.currentUser));
    this.set('application', application);
  }

  @action
  async silentlyLoadApplication(applicationId, includes = []) {
    try {
      const response = await this.applicationCommandService.loadApplication(applicationId, includes);
      this.store.pushPayload(response);
      const application = this.store.peekRecord('application', response.data.id);
      return application;
    } catch(e) {
      this.errorTracker.error(e, this, `silentlyLoadApplication task failed for ${applicationId}.`);
      throw e;
    }
  }

  @task
  *approveOffer(application) {
    try {
      yield this.applicationCommandService.approveOffer(application.id);
      this.applicationStateManager.addToast('Project started');

      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, application.id);
    } catch(e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error approving the project offer. Please refresh and try again.',
        'approveOffer -- error approving project offer app id: ' + application.id
      );
    }
  }

  @restartableTask
  *reloadApplicationTask(application, includes) {
    try {
      const updatedApplication = yield this.silentlyLoadApplication(application.id, includes);
      this.set('application', updatedApplication);
    } catch(e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error reloading the application. Please refresh and try again.',
        'loadApplicationTask -- error reloading app id: ' + application.id,
      );
    }
  }

  @task
  *agreeToLegal(application) {
    try {
      yield this.applicationCommandService.agreeToLegal(application);
      const updatedApplication = yield this.silentlyLoadApplication(application.id, POSTING__APP_SELLER_INCLUDES);
      this.set('application', updatedApplication);
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error attempting to accept legal agreement. Please refresh and try again.',
        'agreeToLegal -- error accepting legal app id: ' + application.id
      );
    }
  }

  resetProperties() {
    this.setProperties({
      application: null,
      hasInitializedThreadMessages: false,
      hasUploadsInRoute: false,
      incomingErrors: {},
      newActivityMessage: '',
      profileOverlay: null,
      profileOverlayType: null,
      profileOverlayModel: null,
    });
  }
}

export default PostingAppController;
